/**
 * TanStack Query hooks for workflow operations.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query"
import { dtaApiService } from "@/services/dtaApi"
import workflowApi from "@/services/workflowApi"
import { invalidateDtaSurfaces } from "./useDtas"

export const workflowKeys = {
  all: ["workflow"] as const,
  approvals: (dtaId: string) => [...workflowKeys.all, "approvals", dtaId] as const,
  status: (dtaId: string | undefined) => [...workflowKeys.all, "status", dtaId ?? null] as const,
}

/**
 * Identifiers a stage transition needs to precisely invalidate the study- and vendor-scoped DTA
 * lists, which are keyed by study / vendor id. The transition responses don't carry these, so the
 * caller (which already holds the DTA record) supplies them.
 */
interface WorkflowTransitionScope {
  studyId?: string
  vendorId?: string
}

/**
 * Refreshes every read affected by a DTA stage transition: all the DTA surfaces (detail, versions,
 * lists) plus the workflow approvals + status. Stage gates vendor visibility (Cedar), so lists must
 * refresh.
 */
function invalidateAfterTransition(
  queryClient: QueryClient,
  dtaId: string,
  scope: WorkflowTransitionScope,
) {
  invalidateDtaSurfaces(queryClient, { dtaId, studyId: scope.studyId, vendorId: scope.vendorId })
  void queryClient.invalidateQueries({ queryKey: workflowKeys.approvals(dtaId) })
  void queryClient.invalidateQueries({ queryKey: workflowKeys.status(dtaId) })
}

interface TransitionArgs {
  reason?: string
  version?: string
}

/**
 * Fetches the workflow status (current stage, allowed transitions, pending approvals) for a DTA.
 */
export function useWorkflowStatus(dtaId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.status(dtaId),
    queryFn: () => workflowApi.getWorkflowStatus(dtaId as string),
    enabled: !!dtaId,
  })
}

/**
 * Submits a DTA for review (DRAFT -> REVIEW).
 */
export function usePublishDta(dtaId: string, scope: WorkflowTransitionScope = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: TransitionArgs = {}) =>
      workflowApi.publish(dtaId, args.reason, args.version),
    onSuccess: () => invalidateAfterTransition(queryClient, dtaId, scope),
  })
}

/**
 * Records an approval on a DTA (REVIEW stage).
 */
export function useApproveDta(dtaId: string, scope: WorkflowTransitionScope = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: TransitionArgs = {}) =>
      workflowApi.approve(dtaId, args.reason, args.version),
    onSuccess: () => invalidateAfterTransition(queryClient, dtaId, scope),
  })
}

/**
 * Activates an approved DTA (APPROVED -> ACTIVE).
 */
export function useActivateDta(dtaId: string, scope: WorkflowTransitionScope = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: TransitionArgs = {}) =>
      workflowApi.activate(dtaId, args.reason, args.version),
    onSuccess: () => invalidateAfterTransition(queryClient, dtaId, scope),
  })
}

/**
 * Deactivates an active DTA (ACTIVE -> INACTIVE).
 */
export function useDeactivateDta(dtaId: string, scope: WorkflowTransitionScope = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: TransitionArgs = {}) => dtaApiService.deactivateDTA(dtaId, args.version),
    onSuccess: () => invalidateAfterTransition(queryClient, dtaId, scope),
  })
}

/**
 * Reactivates an inactive DTA (INACTIVE -> ACTIVE).
 */
export function useReactivateDta(dtaId: string, scope: WorkflowTransitionScope = {}) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (args: TransitionArgs = {}) => dtaApiService.reactivateDTA(dtaId, args.version),
    onSuccess: () => invalidateAfterTransition(queryClient, dtaId, scope),
  })
}

/**
 * Fetches approvals for a DTA, optionally filtered by stage.
 */
export function useApprovals(dtaId: string, stage?: string) {
  const stageFilter = stage && stage.toUpperCase() === "ACTIVE" ? undefined : stage
  return useQuery({
    queryKey: [...workflowKeys.approvals(dtaId), stageFilter] as const,
    queryFn: () => workflowApi.getApprovals(dtaId, stageFilter),
  })
}

/**
 * Submits an approval and invalidates the approvals cache after a short delay to allow for backend
 * eventual consistency.
 */
export function useSubmitApproval(dtaId: string, onApprovalUpdate?: () => void) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (stage: string) => workflowApi.submitApproval(dtaId, stage),
    onSuccess: async () => {
      await new Promise((r) => setTimeout(r, 1000))
      await queryClient.invalidateQueries({ queryKey: workflowKeys.approvals(dtaId) })
      onApprovalUpdate?.()
    },
  })
}
